import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AdminService } from '../../services/AdminService';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const roles = [
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
  { value: 'moderator', label: 'Moderator' }
];

export default function Users() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('');

  // Fetch users
  const { data, isLoading, error } = useQuery(
    ['users', page],
    () => AdminService.getUsers(null, 10),
    {
      keepPreviousData: true
    }
  );

  // Update user role mutation
  const updateRoleMutation = useMutation(
    ({ userId, role }) => AdminService.updateUserRole(userId, role),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        enqueueSnackbar('User role updated successfully', { variant: 'success' });
        setEditDialogOpen(false);
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    (userId) => AdminService.deleteUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        enqueueSnackbar('User deleted successfully', { variant: 'success' });
        setDeleteDialogOpen(false);
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  );

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleRoleUpdate = () => {
    updateRoleMutation.mutate({
      userId: selectedUser.id,
      role: newRole
    });
  };

  const handleUserDelete = () => {
    deleteUserMutation.mutate(selectedUser.id);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => queryClient.invalidateQueries('users')}
        >
          Refresh
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={user.photoURL} alt={user.displayName}>
                      {user.displayName?.[0]}
                    </Avatar>
                    <Typography>{user.displayName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role || 'user'}
                    color={user.role === 'admin' ? 'error' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.disabled ? (
                    <Chip
                      icon={<BlockIcon />}
                      label="Blocked"
                      color="error"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Active"
                      color="success"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {user.createdAt?.toDate 
                    ? user.createdAt.toDate().toLocaleDateString()
                    : user.createdAt instanceof Date 
                      ? user.createdAt.toLocaleDateString()
                      : 'N/A'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit Role">
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(user)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete User">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(user)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              select
              fullWidth
              label="Role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
            >
              {roles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRoleUpdate}
            variant="contained"
            disabled={updateRoleMutation.isLoading}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this user? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUserDelete}
            color="error"
            variant="contained"
            disabled={deleteUserMutation.isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 